import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SignedIn, SignedOut, SignOutButton, useUser, useCompany } from '../../../contexts/AuthContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { usePackChromeIfPresent } from '../../../contexts/PackChromeContext';
import { isSignupChromeAllowed } from '../../../utils/authSignupChrome';
import { Bell, Settings } from 'lucide-react';

/**
 * AppHeader - Shared header component for all authenticated layouts
 *
 * This is a pure presentational component that receives callbacks for interactions.
 * Each layout (Merchant, SO) imports this and provides its own handlers.
 *
 * @param {Function} onMobileMenuClick - Callback when hamburger menu is clicked
 * @param {boolean} showMobileMenu - Whether to show the hamburger menu (default: true)
 * @param {number} sidebarWidth - Width of sidebar for spacer (default: 200)
 *
 * Features:
 * - Dynamic brand logo/name from BrandingContext
 * - Search bar (centered on desktop)
 * - Notifications bell
 * - Settings icon (desktop only)
 * - User profile dropdown with logout
 * - Hamburger menu for mobile sidebar toggle
 */
const AppHeader = ({
  onMobileMenuClick,
  showMobileMenu = true,
  sidebarWidth = 200,
  topOffset = 0
}) => {
  const { user: currentUser } = useUser();
  const { company } = useCompany();
  const { branding, signupEnabled } = useBranding();
  const { towtechSoChrome } = usePackChromeIfPresent();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const platformName = branding.identity.platformName;
  const logoUrl = branding.assets.logoUrl;
  const logoMarkUrl = branding.assets.logoMarkUrl;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate display name - use company name for SO users, or user's full name
  const displayName = !towtechSoChrome && currentUser?.role === 'so' && company?.businessName
    ? company.businessName
    : currentUser?.firstName && currentUser?.lastName
    ? `${currentUser.firstName} ${currentUser.lastName.charAt(0)}.`
    : 'User';

  // Get role-based path prefix
  const getPathPrefix = () => {
    const role = currentUser?.role;
    if (role === 'merchant') return '/merchant';
    if (role === 'so') return '/so';
    if (role === 'superadmin') return '/admin';
    return '/merchant'; // fallback
  };

  return (
    <header
      className="bg-gray-900 text-white h-16 fixed right-0 left-0 z-30 flex items-center"
      style={{ top: topOffset }}
    >
      <div className="flex items-center w-full">
        <div className="flex-1 flex items-center">
          {/* Logo area on desktop (matches sidebar width) */}
          <div
            className="hidden lg:flex shrink-0 items-center justify-center"
            style={{ width: `${sidebarWidth}px` }}
          >
            {sidebarWidth > 100 ? (
              // Expanded: full logo → platformName text
              logoUrl ? (
                <img src={logoUrl} alt={platformName} className="h-8 w-auto max-w-[160px] object-contain" />
              ) : (
                <span className="text-brand font-bold text-xl">{platformName}</span>
              )
            ) : (
              // Collapsed: app icon → first letter
              logoMarkUrl ? (
                <img src={logoMarkUrl} alt={platformName} className="h-8 w-8 object-contain" />
              ) : (
                <span className="text-brand font-bold text-xl">{platformName[0]}</span>
              )
            )}
          </div>

          <div className="flex-1 flex items-center gap-4 min-w-0 px-4">
            {/* Mobile hamburger menu */}
            {showMobileMenu && (
              <button
                className="lg:hidden text-gray-400 hover:text-white"
                onClick={onMobileMenuClick}
                aria-label="Toggle mobile menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            )}

            {/* Search bar */}
            <div className="flex-1">
              <div className="w-full lg:max-w-md lg:mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search"
                    className="w-full bg-gray-800 text-white border border-gray-700 rounded-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  <svg
                    className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M12.9 14.32a8 8 0 111.41-1.41l5.35 5.33-1.42 1.42-5.33-5.34zM8 14A6 6 0 108 2a6 6 0 000 12z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - notifications, settings, profile */}
        <SignedIn>
          <div className="flex items-center space-x-4 px-4">
            <div className="flex items-center space-x-4">
              <button className="text-gray-400 hover:text-white" aria-label="Notifications">
                <Bell className="w-5 h-5" />
              </button>
              <Link
                to={`${getPathPrefix()}/settings`}
                className="hidden lg:block text-gray-400 hover:text-white"
                aria-label="Settings"
              >
                <Settings className="w-5 h-5" />
              </Link>
            </div>

            {/* Profile dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 focus:outline-none"
              >
                {currentUser?.imageUrl ? (
                  <img
                    src={`${currentUser.imageUrl}?width=64&height=64&quality=90&fit=crop`}
                    alt="Profile"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center text-white">
                    {currentUser?.firstName?.[0] || 'U'}
                  </div>
                )}
                <span className="hidden md:inline text-sm md:text-base">{displayName}</span>
                <svg
                  className={`hidden md:block w-5 h-5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                  <Link
                    to={`${getPathPrefix()}/profile`}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    My Profile
                  </Link>
                  <Link
                    to={`${getPathPrefix()}/settings`}
                    className="lg:hidden block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    Settings
                  </Link>
                  <SignOutButton
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      localStorage.removeItem('user');
                    }}
                  >
                    Logout
                  </SignOutButton>
                </div>
              )}
            </div>
          </div>
        </SignedIn>

        <SignedOut>
          <div className="flex space-x-4 px-4">
            {isSignupChromeAllowed({ signupEnabled }) && (
              <Link to="/sign-up" className="text-sm hover:underline">Sign Up</Link>
            )}
            <Link to="/sign-in" className="text-sm hover:underline">Log In</Link>
          </div>
        </SignedOut>
      </div>
    </header>
  );
};

export default AppHeader;
